import { Button } from 'src/components/ui/button';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import Input from 'src/components/Input/Input';
import type { SalesRoleIcp } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';

interface SalesRolePainPointStepProps {
  // Which of the two pain points this step collects.
  painPointNumber: 1 | 2;
  data?: SalesRoleIcp | null;
  // Shows a Prev button when given.
  onPrev?: () => void;
  onSubmit: (icp: SalesRoleIcp) => void;
  titleDescription?: string;
  onCancel: () => void;
}

// A step of the sales role popup collecting a pain point, its solution and a client review.
export const SalesRolePainPointStep = ({
  painPointNumber,
  data,
  onPrev,
  onSubmit,
  titleDescription = '',
  onCancel,
}: SalesRolePainPointStepProps) => {
  const formId = `sales-role-step${painPointNumber}-form`;

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      pf_icp_pain_point: data?.pf_icp_pain_point || '',
      pf_icp_solution: data?.pf_icp_solution || '',
      pf_icp_client_review: data?.pf_icp_client_review || '',
    },
    resolver: yupResolver(
      object({
        pf_icp_pain_point: stringScheme({ required: true }),
        pf_icp_solution: stringScheme({ required: true }),
        pf_icp_client_review: stringScheme({ required: true }),
      }),
    ),
  });

  const submit = (submitData: SalesRoleIcp) => {
    onSubmit(submitData);
  };

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.stopPropagation();

        return handleSubmit(submit)(e);
      }}
    >
      <div>
        <p className={'text-ebony_opacity_50 text-sm font-medium'}>
          {titleDescription ||
            'Designed to engage potential customers, highlight product benefits, and drive conversions.'}
        </p>
      </div>
      <div className={'flex flex-col gap-2.5'}>
        <div>
          <p className={'mt-5 text-ebony_opacity_80 font-semibold text-base'}>
            Pain point #{painPointNumber}
          </p>
        </div>
        <div>
          <Controller
            control={control}
            name={'pf_icp_pain_point'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div className={'flex flex-col w-full'}>
                <Input
                  autoFocus
                  className={'!py-2.5'}
                  type={'text'}
                  placeholder={'Long ticket resolution time.'}
                  label={'Pain point'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                />
              </div>
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name={'pf_icp_solution'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div className={'flex flex-col w-full'}>
                <Input
                  className={'!py-2.5'}
                  type={'textarea'}
                  placeholder={
                    'CoCustomer automates up to 90% of interactions with customers, allowing your team to focus on complex tasks.'
                  }
                  textareaRows={2}
                  label={'Solution'}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                />
              </div>
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name={'pf_icp_client_review'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div className={'flex flex-col w-full'}>
                <Input
                  className={'!py-2.5'}
                  type={'textarea'}
                  placeholder={
                    "The Al performance has been very good. It handles FAQs and many complex questions well, and the escalated tickets I'm seeing come through are ones I wouldn't expect Al to be able to handle. I'm really happy with CoSupport Al customer service solutions. — Matthew Brown, Director of Customer Solutions at Shelterluv."
                  }
                  textareaRows={5}
                  label={`Client's review`}
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                />
              </div>
            )}
          />
        </div>
      </div>
      <div className="flex justify-center mt-5">
        <div className="flex gap-2.5">
          <Button variant="outline" type="button" className="min-w-24" onClick={onCancel}>
            Cancel
          </Button>
          {onPrev && (
            <Button
              variant="outline-light"
              type="button"
              className="min-w-24 flex flex-row gap-[5px]"
              onClick={onPrev}
            >
              <span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="10"
                  viewBox="0 0 9 10"
                  fill="none"
                >
                  <path
                    d="M9 4.4375L2.15438 4.4375L5.29875 1.29312L4.5 0.499998L0 5L4.5 9.5L5.29313 8.70687L2.15438 5.5625L9 5.5625V4.4375Z"
                    fill="#05050D"
                  />
                </svg>
              </span>
              <span>Prev</span>
            </Button>
          )}
          <Button form={formId} disabled={!isValid} className="min-w-24 flex flex-row gap-[5px]">
            <span>Next</span>
            <span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <path
                  d="M0.5 5.5625L7.34562 5.5625L4.20125 8.70688L5 9.5L9.5 5L5 0.5L4.20687 1.29313L7.34562 4.4375L0.5 4.4375V5.5625Z"
                  fill="white"
                />
              </svg>
            </span>
          </Button>
        </div>
      </div>
    </form>
  );
};
